import { Flex, Grid, Box, Section, Heading } from "@radix-ui/themes";
import { Card } from "~/components/home/Card";
import styles from "./treatments.module.css";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";

type Localization = {
  treatmentsTitle: string;
  bracesHeading: string;
  bracesDescription: string;
  bracesAlt: string;
  invisalignHeading: string;
  invisalignDescription: string;
  invisalignAlt: string;
  clearBracesHeading: string;
  clearBracesDescription: string;
  clearBracesAlt: string;
};

const englishLocalization: Localization = {
  treatmentsTitle: "Our Treatments",
  bracesHeading: "Metal Braces",
  bracesDescription:
    "Todays metal braces are more comfortable, less visible and lighter than ever before.",
  bracesAlt: "Image of metal braces.",
  clearBracesHeading: "Clear Braces",
  clearBracesDescription:
    "These clear braces blend in with your teeth, so they're less noticeable than traditional braces.",
  clearBracesAlt: "Image of clear braces.",
  invisalignHeading: "Invisalign",
  invisalignDescription:
    "The invisalign system is made up of a series of custom, clear trays called aligners.",
  invisalignAlt: "Image of Invisalign aligners.",
};

const spanishLocalization: Localization = {
  treatmentsTitle: "Nuestros tratamientos",
  bracesHeading: "Brackets de metal",
  bracesDescription:
    "Los brackets de metal de hoy son más cómodos, menos visibles y más ligeros que nunca.",
  bracesAlt: "Imagen de brackets de metal.",
  clearBracesHeading: "Brackets transparentes",
  clearBracesDescription:
    "Estos brackets transparentes se mezclan con tus dientes, por lo que son menos visibles que los brackets tradicionales.",
  clearBracesAlt: "Imagen de brackets",
  invisalignHeading: "Invisalign",
  invisalignDescription:
    "El sistema invisalign está compuesto por una serie de bandejas transparentes personalizadas llamadas alineadores.",
  invisalignAlt: "Imagen de alineadores Invisalign.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function Treatments() {
  const getLocalization = useLocalization(localized);
  return (
    <Section pt="0" px={{ initial: "4", sm: "9" }}>
      <Flex direction="column" align="center">
        <Heading
          as="h2"
          size={{ initial: "8", sm: "9" }}
          className={styles.treatmentHeading}
        >
          {getLocalization("treatmentsTitle")}
        </Heading>
        <Grid
          columns="12"
          rows="auto"
          gapX={{ initial: "2", sm: "4", md: "7" }}
        >
          <Box height="100%" gridColumn={{ initial: "1 / 13", sm: "1 / 5" }}>
            <Card
              imageSrc="/treatments/metal.jpg"
              imageSrcSet="/optimized/treatments-metal-352.webp 352w, /optimized/treatments-metal-704.webp 704w"
              imageSizes="(max-width: 767px) calc(100vw - 64px), 31vw"
              heading={getLocalization("bracesHeading")}
              description={getLocalization("bracesDescription")}
              alt={getLocalization("bracesAlt")}
              to="/metal-braces"
            />
          </Box>
          <Box height="100%" gridColumn={{ initial: "1 / 13", sm: "5 / 9" }}>
            <Card
              imageSrc="/treatments/miami-braces-clear-braces.jpg"
              imageSrcSet="/optimized/treatments-miami-braces-clear-braces-352.webp 352w, /optimized/treatments-miami-braces-clear-braces-704.webp 704w"
              imageSizes="(max-width: 767px) calc(100vw - 64px), 31vw"
              heading={getLocalization("clearBracesHeading")}
              description={getLocalization("clearBracesDescription")}
              alt={getLocalization("clearBracesAlt")}
              to="/clear-braces"
            />
          </Box>

          <Box height="100%" gridColumn={{ initial: "1 / 13", sm: "9 / 13" }}>
            <Card
              imageSrc="/treatments/invisalign.jpg"
              imageSrcSet="/optimized/treatments-invisalign-352.webp 352w, /optimized/treatments-invisalign-704.webp 704w"
              imageSizes="(max-width: 767px) calc(100vw - 64px), 31vw"
              heading={getLocalization("invisalignHeading")}
              description={getLocalization("invisalignDescription")}
              alt={getLocalization("invisalignAlt")}
              to="/invisalign"
            />
          </Box>
        </Grid>
      </Flex>
    </Section>
  );
}
